import {
  getQuest,
  inventoryGrids,
  QUESTS,
  TRADERS,
  type GridAreaId,
  type GridState,
  type Inventory,
  type QuestDef,
  type QuestState,
  type TraderDef,
} from "@rietz/shared";

/**
 * The quest log's arithmetic, with no React. Three rules: a quest the profile
 * has NO STATE for is locked and not shown (the settlement materializes a
 * successor when its predecessor is done); a state for a quest that no longer
 * exists is dropped; progress is clamped to [0, count] and full progress counts
 * as done however the flag was written — the settlement's normalization. ORDER
 * is the catalogue's, grouped by trader in `TRADERS` order.
 */

/** One line of the log: what to do, how far along, and whether it is over. */
export interface QuestRow {
  def: QuestDef;
  /** Clamped into [0, def.goal.count]. */
  progress: number;
  done: boolean;
}

/** One trader's section. Present even when empty — a trader with nothing open
 *  is a statement in itself, and a section that vanishes moves the two below it. */
export interface TraderQuests {
  trader: TraderDef;
  quests: QuestRow[];
}

export function questBoard(
  states: readonly QuestState[],
  catalog: readonly QuestDef[] = QUESTS,
): TraderQuests[] {
  const byId = new Map<string, QuestState>();
  for (const s of states) {
    // First write wins, matching the settlement's own de-duplication.
    if (!byId.has(s.id)) byId.set(s.id, s);
  }

  const out: TraderQuests[] = TRADERS.map((trader) => ({ trader, quests: [] }));
  const byTrader = new Map<string, QuestRow[]>(out.map((t) => [t.trader.id, t.quests]));

  for (const def of catalog) {
    const state = byId.get(def.id);
    if (state === undefined) continue; // rule 1: locked
    const rows = byTrader.get(def.traderId);
    if (rows === undefined) continue; // a quest whose trader is gone
    const progress = clamp(state.progress, def.goal.count);
    rows.push({ def, progress, done: state.done === true || progress >= def.goal.count });
  }
  return out;
}

/**
 * Quest ids → display names for the raid-end summary. An unresolved id is
 * printed AS the id rather than dropped.
 */
export function questNames(ids: readonly string[]): string[] {
  return ids.map((id) => getQuest(id)?.name ?? id);
}

// ---------------------------------------------------------------------------
// The handover: a `bringItems` quest takes nothing by itself; the player says
// WHICH instances go across the counter. The client's copy of the server's
// rule.
// ---------------------------------------------------------------------------

/** One instance the player could hand over, and where it is lying. */
export interface TurnInCandidate {
  iid: string;
  /** Units in this one instance; at least 1. The last one handed over may be
   *  SPLIT by the server when it overshoots what the quest still wants. */
  stack: number;
  /** Where it lies, as the dialogue prints it: "Stash", "Pockets", … */
  source: string;
}

/** The kit's three grids, in the words the gear screen uses for them. */
const AREA_LABELS: Readonly<Record<GridAreaId, string>> = {
  pockets: "Pockets",
  rig: "Rig",
  backpack: "Backpack",
};

/**
 * Every instance of `defId` the player could hand over: the stash first, then
 * the kit's grids in scan order. TOP LEVEL ONLY — not in a rucksack, not in a
 * slot — the same exclusions the server applies. The ORDER is the server's
 * consumption order, so the row that gets split is the last on screen.
 */
export function eligibleTurnIns(
  defId: string,
  stash: GridState,
  kit: Inventory,
): TurnInCandidate[] {
  const out: TurnInCandidate[] = [];
  const collect = (grid: GridState, source: string): void => {
    for (const placed of grid.items) {
      const item = placed.item;
      if (item.defId !== defId) continue;
      const stack = Math.floor(item.stack);
      if (!Number.isFinite(stack) || stack < 1) continue;
      out.push({ iid: item.iid, stack, source });
    }
  };

  collect(stash, "Stash");
  for (const { area, grid } of inventoryGrids(kit)) collect(grid, AREA_LABELS[area]);
  return out;
}

/** A stored progress value, made safe: integral, non-negative, capped. */
function clamp(n: number, count: number): number {
  if (!Number.isFinite(n) || n <= 0) return 0;
  const v = Math.floor(n);
  return v > count ? count : v;
}
